import {useService} from '@cindedi/react-application';
import {abort} from '@cindedi/request-utilities/abort';
import {isRecord} from '@cindedi/utilities/isRecord';
import {truncate} from '@cindedi/utilities/truncate';
import type {MetaFunction, LoaderFunction} from '@remix-run/node';
import {json} from '@remix-run/node';
import {Link, useLoaderData} from '@remix-run/react';
import {PublicLayout} from '~/domains/organization/components/PublicLayout';

interface StreamMemberPublic {
  memberId: string;
  memberName: string;
  memberSlug: string;
  roleName: string;
}

interface AssociatedProduction {
  id: string;
  title: string;
  year: string;
  type: string;
  author: string;
}

interface AssociatedPublication {
  id: string;
  title: string;
  year: string;
  type: string;
  authors: string;
}

export interface LinhasDePesquisa$SlugLoaderData {
  researchStream: Research.PublicResearchStreamDto;
  members: StreamMemberPublic[];
  productions: AssociatedProduction[];
  publications: AssociatedPublication[];
}

export const meta: MetaFunction = function LinhasDePesquisa$SlugMeta({data}) {
  return isRecord(data) && data.researchStream
    ? [
        {title: `${data.researchStream.name} | Cindedi`},
        {
          name: 'description',
          content: truncate(data.researchStream.textDescription, 155),
        },
      ]
    : [{title: 'Linha de Pesquisa - Cindedi'}];
};

export const loader: LoaderFunction = async function LinhasDePesquisa$SlugLoader({
  context: {use},
  params,
}) {
  const ResearchStream = use('ResearchStream');
  const {slug} = params;

  if (!slug) return abort(404);

  const researchStream = await ResearchStream.find({where: {slug}});

  if (researchStream == null) return abort(404);

  const members = await ResearchStream.getMembers(researchStream.id);
  const productions = await ResearchStream.getProductions(researchStream.id);
  const publications = await ResearchStream.getPublications(researchStream.id);

  return json({
    researchStream,
    members: members.map((m) => ({
      memberId: m.memberId,
      memberName: m.memberName,
      memberSlug: m.memberSlug,
      roleName: m.roleName,
    })),
    productions,
    publications,
  });
};

export default function LinhasDePesquisa$SlugView() {
  const {researchStream, members, productions, publications} =
    useLoaderData<LinhasDePesquisa$SlugLoaderData>();
  useService('organization/titlebar').setTitle(researchStream.name);

  const hasMembers = members.length > 0;
  const hasProductions = productions.length > 0;
  const hasPublications = publications.length > 0;
  const hasRightColumn = hasProductions || hasPublications;

  return (
    <PublicLayout>
      <div className="flex flex-col flex-1 gap-2 pt-4 max-w-full">
        <div className="flex gap-2 text-primary-8 text-sm items-center mt-1 bg-primary-1 p-4 rounded mb-4">
          <strong>Coordenador(a):</strong>
          <Link to={`/membros/${researchStream.principalInvestigator.slug}`}>
            {researchStream.principalInvestigator.name}
          </Link>
        </div>

        <div dangerouslySetInnerHTML={{__html: researchStream.description}} />

        {(hasMembers || hasRightColumn) && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-8">
            {hasMembers && (
              <div>
                <h2 className="text-lg font-semibold text-shade-12 mb-4">Membros</h2>
                <ul role="list" className="space-y-2">
                  {members.map((member) => (
                    <li key={member.memberId} className="bg-shade-2 rounded p-3">
                      <Link
                        to={`/membros/${member.memberSlug}`}
                        className="flex flex-col hover:text-primary-11"
                      >
                        <span className="font-medium text-primary-12">
                          {member.memberName}
                        </span>
                        <span className="text-sm text-shade-10">{member.roleName}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {hasRightColumn && (
              <div className="space-y-8">
                {hasProductions && (
                  <div>
                    <h2 className="text-lg font-semibold text-shade-12 mb-4">Trabalhos</h2>
                    <ul role="list" className="space-y-2">
                      {productions.map((prod) => (
                        <li key={prod.id} className="bg-shade-2 rounded p-3">
                          <div className="flex flex-col">
                            <div className="flex gap-2 text-shade-10 text-sm mb-1">
                              <span>{prod.type}</span>•<span>{prod.year}</span>
                            </div>
                            <span className="font-medium text-primary-12">{prod.title}</span>
                            <span className="text-sm text-shade-11">{prod.author}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {hasPublications && (
                  <div>
                    <h2 className="text-lg font-semibold text-shade-12 mb-4">Publicações</h2>
                    <ul role="list" className="space-y-2">
                      {publications.map((pub) => (
                        <li key={pub.id} className="bg-shade-2 rounded p-3">
                          <div className="flex flex-col">
                            <div className="flex gap-2 text-shade-10 text-sm mb-1">
                              <span>{pub.type}</span>•<span>{pub.year}</span>
                            </div>
                            <span className="font-medium text-primary-12">{pub.title}</span>
                            <span className="text-sm text-shade-11">{pub.authors}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
